import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useLogin } from '@/features/auth/useAuth'
import { ApiError } from '@/api/client'
import { useIsPhone } from '@/lib/useMediaQuery'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { MadeBy } from '@/components/MadeBy'
import { useT } from '@/i18n'
import { authMessages } from '@/i18n/messages/auth'
import './landing.css'

/** What the platform actually does — no invented numbers, just the real capabilities. */
const CAPABILITIES = [
  {
    title: 'capRtkTitle',
    body: 'capRtkBody',
    icon: (
      <>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </>
    ),
  },
  {
    title: 'capAufmassTitle',
    body: 'capAufmassBody',
    icon: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
      </>
    ),
  },
  {
    title: 'capDashboardTitle',
    body: 'capDashboardBody',
    icon: (
      <>
        <path d="M3 3v18h18" />
        <path d="M7 14l3-4 4 3 5-7" />
      </>
    ),
  },
  {
    title: 'capExportTitle',
    body: 'capExportBody',
    icon: (
      <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </>
    ),
  },
] as const

/** Glowing nodes over the map — each with its own size and cadence so they never pulse in unison. */
const NODES = [
  { top: '26%', left: '56%', size: '16px', dur: '5.5s', delay: '0s' },
  { top: '43%', left: '70%', size: '11px', dur: '4.4s', delay: '1.4s' },
  { top: '61%', left: '63%', size: '18px', dur: '6.8s', delay: '3.0s' },
  { top: '34%', left: '81%', size: '10px', dur: '5.0s', delay: '2.1s' },
  { top: '70%', left: '77%', size: '14px', dur: '6.0s', delay: '4.3s' },
  { top: '52%', left: '86%', size: '12px', dur: '4.8s', delay: '0.8s' },
  { top: '20%', left: '72%', size: '9px',  dur: '5.3s', delay: '3.7s' },
] as const

export function LandingPage() {
  const rootRef = useRef<HTMLDivElement>(null)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const login = useLogin()
  const isPhone = useIsPhone()
  const t = useT(authMessages)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    login.mutate({ username, password, rememberMe })
  }

  // Only a 401 is bad credentials; a 5xx is a server problem and must not read as "wrong password".
  const message =
    login.error instanceof ApiError && login.error.status === 429
      ? t('errTooMany')
      : login.error instanceof ApiError && login.error.status === 401
        ? t('errWrongCredentials')
        : login.error instanceof ApiError && login.error.status >= 500
          ? t('errServer', { status: String(login.error.status) })
            + (login.error.detail ? ` — ${login.error.detail}` : '')
          : login.error
            ? t('errGeneric')
            : null

  // Subtle desktop cursor parallax — sets --mx/--my in [-1,1] on the root; the CSS moves each
  // layer by a different factor. Skipped for coarse pointers (touch) and reduced-motion.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const fine = window.matchMedia('(pointer: fine)').matches
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!fine || still) return

    let raf = 0
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const mx = (e.clientX / window.innerWidth) * 2 - 1
        const my = (e.clientY / window.innerHeight) * 2 - 1
        el.style.setProperty('--mx', mx.toFixed(3))
        el.style.setProperty('--my', my.toFixed(3))
      })
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      cancelAnimationFrame(raf)
    }
  }, [])

  const inputClass =
    'mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white ' +
    'placeholder-white/30 outline-none transition focus:border-brand-500/60 focus:bg-white/[0.07] ' +
    'focus:ring-2 focus:ring-brand-500/30'

  return (
    <div ref={rootRef} className="lander text-white">
      {/* ── animated background (decorative) ─────────────────────────────────── */}
      <div className="lander__bg" aria-hidden="true">
        <div className="lander__layer lander__layer--map">
          <div className="lander__img lander__map" />
        </div>
        <div className="lander__layer lander__layer--net">
          <div className="lander__img lander__base" />
          <div className="lander__img lander__pulse" />
        </div>
        <div className="lander__layer lander__layer--atmo">
          <div className="lander__img lander__atmo" />
        </div>
        <div className="lander__sweep" />
        <div className="lander__scrim" />
        {NODES.map((n, i) => (
          <span
            key={i}
            className="lander__node"
            style={{
              top: n.top,
              left: n.left,
              animationDelay: n.delay,
              ['--size' as string]: n.size,
              ['--dur' as string]: n.dur,
            }}
          />
        ))}
      </div>

      {/* ── foreground ───────────────────────────────────────────────────────── */}
      <div className="relative z-10 flex min-h-screen min-h-[100dvh] flex-col">
        {/* Hero: login panel left-of-center, capability cards right */}
        <main id="top" className="flex flex-1 items-center px-4 py-10 pt-[max(2.5rem,env(safe-area-inset-top))] sm:px-6">
          <div className="mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[minmax(0,560px)_1fr]">
            {/* Login / hero panel */}
            <section className="w-full min-w-0 rounded-[22px] border border-white/10 bg-neutral-950/55 p-5 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-9">
              <div className="flex items-center gap-2.5">
                <img src="/logo_transparent.png" alt="" aria-hidden className="h-8 w-8 object-contain" />
                <span className="text-lg font-semibold tracking-tight">MIH GIS</span>
                <div className="ml-auto">
                  <LanguageSwitcher tone="dark" />
                </div>
              </div>
              <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-500">
                {t('tagline')}
              </p>
              <h1 className="mt-2 text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl">
                {t('heroInfrastructure')}
                <br />
                {t('heroMeasured')} <span className="text-brand-500">{t('heroConnected')}</span>
              </h1>
              <p className="mt-3 max-w-md text-pretty text-sm leading-relaxed text-white/60">
                {t('heroBody')}
              </p>

              <form onSubmit={onSubmit} className="mt-7 space-y-4">
                {/*
                  Labelled "Username", not "Email": 173 of 181 AU accounts authenticate with
                  users.Username. The value posts to the existing login exactly as before.
                */}
                <label className="block">
                  <span className="text-sm font-medium text-white/80">{t('username')}</span>
                  <input
                    type="text"
                    autoComplete="username"
                    required
                    autoFocus={!isPhone}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={inputClass}
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-white/80">{t('password')}</span>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputClass + ' pr-11'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                      aria-pressed={showPassword}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-white/40 transition hover:text-white/80"
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </label>

                <div className="flex flex-col items-start gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                  <label className="flex items-center gap-2 text-white/60">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="rounded border-white/20 bg-white/5 text-brand-600 focus:ring-brand-500/30"
                    />
                    {t('keepSignedIn')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgot((v) => !v)}
                    className="text-white/50 transition hover:text-white/80"
                  >
                    {t('forgotPassword')}
                  </button>
                </div>

                {showForgot && (
                  <p className="text-xs text-white/50">
                    {t('forgotHelp')}
                  </p>
                )}

                {message && (
                  <p role="alert" className="text-sm text-status-critical">
                    {message}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={login.isPending}
                  className="w-full rounded-xl bg-brand-600 px-3 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-900/30 transition hover:bg-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:opacity-60"
                >
                  {login.isPending ? t('signingIn') : t('signIn')}
                </button>

                <div className="flex items-center gap-2 pt-1 text-[11px] text-white/35">
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="inline-flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {t('secureAccess')}
                  </span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
              </form>
            </section>

            {/* Capability cards — what the platform does. Desktop/tablet only. */}
            <aside id="capabilities" className="hidden lg:block">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
                {t('whatsInside')}
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {CAPABILITIES.map((c) => (
                  <div
                    key={c.title}
                    className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-md transition hover:border-brand-500/30 hover:bg-white/[0.06]"
                  >
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/10 ring-1 ring-brand-500/20">
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="text-brand-500" aria-hidden="true">
                        {c.icon}
                      </svg>
                    </span>
                    <h2 className="mt-4 text-base font-semibold text-white">{t(c.title)}</h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-white/55">{t(c.body)}</p>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </main>

        <footer className="px-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] text-center text-xs text-white/30">
          <p>MIH GIS · mihgis.com</p>
          <MadeBy className="mt-1 text-[11px] leading-snug text-white/30" />
        </footer>
      </div>
    </div>
  )
}
